import {CountryPicker} from "../../CountryPicker/CountryPicker";
import {AsYouType, parsePhoneNumber} from "libphonenumber-js/max";
import {useEffect, useState} from "react";
import {FlagImage} from "react-international-phone";
import type {CountryCode} from "libphonenumber-js/max";

import {getCallingCountries} from "../services/countryDataService";

interface Country {
    name: string;
    alpha2: string;
    countryCallingCodes: readonly string[];
    emoji: string;
}

interface PhoneHQPhoneNumberInputProps {
    phoneNumber: string | undefined;
    onPhoneNumberChange: (phoneNumber: number) => void;
    onBlur?: () => void;
    error?: string;
    name?: string;
    label?: string;
    placeholder?: string;
}

export const PhoneHQPhoneNumberInput = ({
    phoneNumber,
    onPhoneNumberChange,
    error,
    onBlur,
    name,
    label,
    placeholder = "",
}: PhoneHQPhoneNumberInputProps) => {
    const [typedNumber, setTypedNumber] = useState(phoneNumber ?? "");
    const [selectedCountry, setSelectedCountry] = useState<{isoCountry: string; prefix: string} | null>({
        isoCountry: "US",
        prefix: "+1",
    });

    useEffect(() => {
        try {
            const parsed = parsePhoneNumber(`+${phoneNumber}`);
            const country = getCallingCountries().find(c => c.alpha2 === parsed?.country);
            setTypedNumber(new AsYouType(country?.alpha2 as CountryCode).input(parsed.nationalNumber));
            setSelectedCountry({
                isoCountry: country?.alpha2 ?? "US",
                prefix: country?.countryCallingCodes[0] ?? "+1",
            });
        } catch {
            setTypedNumber(phoneNumber?.toString() ?? "");
        }
    }, []);

    const handleCountrySelect = (country: Pick<Country, "alpha2" | "countryCallingCodes">) => {
        setSelectedCountry({
            isoCountry: country.alpha2,
            prefix: country.countryCallingCodes[0],
        });
        if (typedNumber) {
            const parsed = parsePhoneNumber(`${country.countryCallingCodes[0] + typedNumber.replace(/\D/g, "")}`);
            setTypedNumber(new AsYouType(country.alpha2 as CountryCode).input(parsed?.nationalNumber));
            onPhoneNumberChange(Number(parsed?.number.replace(/\D/g, "")));
        } else {
            setTypedNumber("");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val.startsWith("+") || val.startsWith("00")) {
            try {
                const parsed = val.startsWith("00") ? parsePhoneNumber(val.replace(/^00/, "+")) : parsePhoneNumber(val);
                if (parsed) {
                    onPhoneNumberChange(Number(val.replace(/\D/g, "")));
                    setSelectedCountry({
                        isoCountry: parsed.country ?? "US",
                        prefix: `+${parsed.countryCallingCode}`,
                    });
                }
            } catch {
                // ignore parse errors while typing
            }
        } else {
            onPhoneNumberChange(Number((selectedCountry?.prefix ?? "") + val.replace(/\D/g, "")));
        }
        const formatted = new AsYouType(selectedCountry?.isoCountry as CountryCode).input(val);
        setTypedNumber(formatted);
    };

    return (
        <div className="w-full flex flex-col gap-1.5">
            {label && (
                <label className="text-[13px] font-semibold text-zinc-900 leading-4">{label}</label>
            )}

            <div
                className={`flex items-center gap-2 bg-surface border rounded-[10px] min-h-9 pl-3 pr-2 py-2 ${
                    error ? "border-rose-400" : "border-zinc-300"
                } focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100`}
            >
                <CountryPicker
                    value={selectedCountry?.isoCountry}
                    onSelect={handleCountrySelect}
                    trigger={
                        <button
                            type="button"
                            className="flex items-center justify-center size-5 rounded overflow-hidden hover:opacity-90 cursor-pointer"
                            aria-label="Select country"
                        >
                            <FlagImage
                                size={20}
                                iso2={selectedCountry?.isoCountry.toLowerCase()}
                                className="block size-5"
                            />
                        </button>
                    }
                />

                <input
                    type="tel"
                    name={name}
                    value={typedNumber}
                    onChange={handleChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none focus:outline-none text-[13px] font-medium text-zinc-900 placeholder:text-zinc-500 leading-4"
                />
            </div>

            {typeof error === "string" && error.length > 0 && (
                <div className="text-rose-500 text-xs">{error}</div>
            )}
        </div>
    );
};
